import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS, appChannel } from 'config/track-config';
import { NotificationStatus, NotificationType } from 'models/Notification';
import { MultiShipmentPayload, Shipment } from 'models/shipments';
import { AppAsyncThunkOpts, SearchError } from 'models/Store';
import axiosInstance from 'utils/axios.utils';
import { transformShipmentResponse } from 'utils/shipment.utils';

import { deleteSession } from '../../customer';
import { clearNotification, createNotification } from '../../notification';
import { setServiceUnavailable } from '../../outage/outage.slice';
import { AppDispatch } from '../../store';
import { GetShipmentErrorData } from './getShipment.thunk';

export const queryShipments = createAsyncThunk<
  {
    successfulResponses: Shipment[];
    invalidIds: string[];
    notTrackableIds: string[];
  },
  { trackingIds: string[]; ignoreErrors?: boolean },
  AppAsyncThunkOpts<SearchError>
>('shipment/queryShipments', async ({ trackingIds, ignoreErrors = false }, thunkAPI) => {
  try {
    const responseShipment = await apiQueryShipment(trackingIds);
    const shipmentPayload: MultiShipmentPayload = responseShipment.data;
    const successfulResponses: Shipment[] = [],
      invalidIds: string[] = [],
      notTrackableIds: string[] = [];

    shipmentPayload.forEach((datum) => {
      const { status, shipment, trackingIds } = datum;
      if (+status === 200 && shipment) {
        const transformedResp = transformShipmentResponse(shipment);
        transformedResp && successfulResponses.push(transformedResp);
      }
      if (+status === 400) invalidIds.push(trackingIds[0]);
      if (+status === 404) notTrackableIds.push(trackingIds[0]);
    });
    return { successfulResponses, invalidIds, notTrackableIds };
  } catch (error) {
    const authError = error as AuthError;
    if (
      (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
      (authError.isLoginRequiredError && authError.isLoginRequiredError())
    ) {
      const status = 401;
      if (!ignoreErrors) await handleQueryShipmentError(thunkAPI.dispatch, status);
      return thunkAPI.rejectWithValue({
        status,
        error: false,
      });
    }

    if (axios.isAxiosError(error)) {
      const axiosErr = error as AxiosError;
      const status = axiosErr.response?.status;
      const data = axiosErr.response?.data as GetShipmentErrorData | undefined;
      if (status && !ignoreErrors) handleQueryShipmentError(thunkAPI.dispatch, status, data);
      return thunkAPI.rejectWithValue({
        status,
        trackingIds: axiosErr.response?.data,
        error: axiosErr.isAxiosError,
      });
    }
    throw error;
  }
});

/**
 * Makes an API call to query/search shipment from Shipments Gateway API
 * @param trackingIds An array of trackingIds to query/search
 * @param apChannelName (optional) A string passed through with the header to identify which app/page is making the call
 */
const apiQueryShipment = (trackingIds: string[], apChannelName: string = appChannel.detail) => {
  const requestConfig = APP_REQUEST_CONFIGS.shipment;
  requestConfig.headers.AP_CHANNEL_NAME = apChannelName;
  return axiosInstance.get(apiEndpoint.shipmentsV1, {
    params: { trackingIds: trackingIds.toString() },
    ...requestConfig,
  });
};

const handleQueryShipmentError = (dispatch: AppDispatch, errStatus: number, error?: GetShipmentErrorData) => {
  const errorNotification = (
    message = errorContent.generic,
    type: string = NotificationType.ALERT,
    status: string = NotificationStatus.ERROR
  ) => {
    const messagePayload = { type, status, message };
    dispatch(createNotification(messagePayload));
  };

  switch (errStatus) {
    case 401:
      dispatch(clearNotification());
      dispatch(deleteSession());
      errorNotification(errorContent.expiredSession, NotificationType.ALERT, NotificationStatus.INFO);
      break;
    case 503:
      dispatch(clearNotification());
      const errorDesc = error?.errorDescription ?? null;
      dispatch(setServiceUnavailable(errorDesc));
      break;
    case 500:
      errorNotification(errorContent.watchlist);
      break;
    default:
      errorNotification();
      break;
  }
};
